import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import type { RefObject } from 'react'
import whiteImage from '../images/white.png'

const CONTENT_WIDTH = 190 // this are caluclations: 210 (A4 page width) - 2*10mm for margins
const CONTENT_HEIGHT = 285 // 297 (A4 page height)  -  2*6mm for margins
const VERTICAL_MARGIN = 6 // 24px to mm
const HORIZONTAL_MARGIN = 10

// Fetches an image URL and returns a same-origin blob URL that html2canvas can capture without
// tainting the canvas. Returns null if the fetch fails (e.g. CORS rejection or network error),
// in which case the static map swap is skipped entirely.
const fetchAsBlobUrl = async (src: string): Promise<string | null> => {
  try {
    const response = await fetch(src)
    if (!response.ok) return null
    const blob = await response.blob()
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}

// Swaps any .pdf-static-map <img> elements into view, replacing their src with a same-origin
// blob URL and hiding the sibling iframe. Returns a cleanup function to restore the DOM.
const prepareStaticMaps = async (): Promise<() => void> => {
  const imgs = Array.from(
    document.querySelectorAll<HTMLImageElement>('.pdf-static-map')
  )
  const cleanups: Array<() => void> = []

  await Promise.all(
    imgs.map(async (img) => {
      const staticSrc = img.getAttribute('data-src') ?? ''
      const blobUrl = await fetchAsBlobUrl(staticSrc)
      if (!blobUrl) return

      const iframe = img.previousElementSibling
      const prevIframeDisplay =
        iframe instanceof HTMLIFrameElement ? iframe.style.display : null

      img.src = blobUrl
      img.style.display = 'block'
      if (iframe instanceof HTMLIFrameElement) iframe.style.display = 'none'

      cleanups.push(() => {
        img.removeAttribute('src')
        img.style.display = 'none'
        if (iframe instanceof HTMLIFrameElement && prevIframeDisplay !== null) {
          iframe.style.display = prevIframeDisplay
        }
        URL.revokeObjectURL(blobUrl)
      })
    })
  )

  return () => {
    cleanups.forEach((fn) => {
      fn()
    })
  }
}

const usePdfExport = (
  pdfContent: RefObject<HTMLInputElement> | RefObject<HTMLInputElement>[],
  title: string,
  setIsDownloading?: (v: boolean) => void,
  quality?: number,
  generatePDFHeader?: (doc: jsPDF) => void,
  offSetPDFHeader: number = 0
) => {
  const handleDownloadPdf = async () => {
    if (setIsDownloading) setIsDownloading(true)

    const restoreStaticMaps = await prepareStaticMaps()

    const addLongPageToPdf = (
      longElement: { data: string; height: number },
      pdf: jsPDF
    ) => {
      let position = VERTICAL_MARGIN
      let remainingElementHeight = longElement.height - CONTENT_HEIGHT

      pdf.addImage(
        longElement.data,
        'JPEG',
        HORIZONTAL_MARGIN,
        position,
        CONTENT_WIDTH,
        longElement.height
      )
      pdf.addImage(whiteImage, 'JPEG', 0, 285, CONTENT_WIDTH, 12)

      while (remainingElementHeight >= 0) {
        position = remainingElementHeight - longElement.height
        pdf.addPage()
        pdf.addImage(
          longElement.data,
          'JPEG',
          HORIZONTAL_MARGIN,
          position + VERTICAL_MARGIN,
          CONTENT_WIDTH,
          longElement.height
        )
        remainingElementHeight =
          remainingElementHeight - CONTENT_HEIGHT - VERTICAL_MARGIN
      }
    }

    if (pdfContent) {
      let childrenList
      if (Array.isArray(pdfContent)) {
        childrenList = pdfContent.flatMap((pdf) =>
          pdf?.current ? Array.from(pdf.current.children) : []
        )
      } else if (pdfContent.current) {
        childrenList = Array.from(pdfContent.current.children)
      }
      if (!childrenList) {
        restoreStaticMaps()
        if (setIsDownloading) setIsDownloading(false)
        return
      }

      const promises = childrenList.map((child) =>
        html2canvas(child as HTMLElement, {
          useCORS: true,
          allowTaint: true
        }).then((canvas) => {
          const imgData = canvas.toDataURL('image/jpeg', quality || 1)
          const imgHeight = (canvas.height * CONTENT_WIDTH) / canvas.width
          return {
            data: imgData,
            height: imgHeight
          }
        })
      )

      Promise.all(promises)
        .then((canvasList) => {
          // eslint-disable-next-line new-cap
          const pdf = new jsPDF()
          let position = VERTICAL_MARGIN + offSetPDFHeader
          let remainingHeight = CONTENT_HEIGHT
          let hasFirstPage = false
          generatePDFHeader?.(pdf)

          canvasList.forEach((canvasElement, i) => {
            if (canvasElement.height < remainingHeight) {
              pdf.addImage(
                canvasElement.data,
                'JPEG',
                HORIZONTAL_MARGIN,
                position,
                CONTENT_WIDTH,
                canvasElement.height
              )
              hasFirstPage = true
              remainingHeight =
                remainingHeight - canvasElement.height - VERTICAL_MARGIN
              position += canvasElement.height + VERTICAL_MARGIN
            } else {
              if (canvasElement.height > CONTENT_HEIGHT) {
                if (hasFirstPage) pdf.addPage()
                addLongPageToPdf(canvasElement, pdf)
                if (i !== canvasList.length - 1) pdf.addPage()
                return
              }
              position = VERTICAL_MARGIN
              pdf.addPage()
              pdf.addImage(
                canvasElement.data,
                'JPEG',
                HORIZONTAL_MARGIN,
                position,
                CONTENT_WIDTH,
                canvasElement.height
              )
              remainingHeight =
                CONTENT_HEIGHT - canvasElement.height - VERTICAL_MARGIN
              position = position + canvasElement.height + VERTICAL_MARGIN
            }
          })
          pdf.save(title)
        })
        .finally(() => {
          restoreStaticMaps()
          if (setIsDownloading) setIsDownloading(false)
        })
    }
  }
  return () => handleDownloadPdf()
}

export default usePdfExport
